import { useState } from "react";
import { Bell, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import { submitReleaseInterest } from "@/lib/releaseInterest";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";

/**
 * "Tell me when [series] is ready" — a digital-launch notification
 * signal, deliberately lighter and distinct from PrintInterestButton
 * (Command 02A §10). Used for series that have no released product yet
 * (Level 1, Level 2, future series) — NOT a print-edition interest.
 */
export default function ReleaseInterestButton({
  seriesId,
  seriesName,
  sourcePage,
  label,
  className = "",
  style,
  testId = "release-interest-trigger",
  accentColor = "#004D40",
}) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("form");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [contactMethod, setContactMethod] = useState("email");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [consent, setConsent] = useState(false);

  const resetForm = () => {
    setStep("form");
    setName("");
    setContactMethod("email");
    setEmail("");
    setWhatsapp("");
    setConsent(false);
    setError("");
  };

  const onOpenChange = (next) => {
    setOpen(next);
    if (next) track("release_interest_open", { series_id: seriesId, source_page: sourcePage });
    else setTimeout(resetForm, 200);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) return setError("Please enter your name.");
    if (contactMethod === "email" && !email.trim()) return setError("Please enter your email, or switch to WhatsApp.");
    if (contactMethod === "whatsapp" && !whatsapp.trim()) return setError("Please enter your WhatsApp number, or switch to email.");
    if (!consent) return setError("Please confirm the consent checkbox below.");

    setSubmitting(true);
    track("release_interest_submit", { series_id: seriesId, source_page: sourcePage });
    try {
      await submitReleaseInterest({
        name: name.trim(),
        email: contactMethod === "email" ? email.trim() : undefined,
        whatsapp: contactMethod === "whatsapp" ? whatsapp.trim() : undefined,
        series_id: seriesId,
        source_page: sourcePage || undefined,
        consent: true,
      });
      track("release_interest_success", { series_id: seriesId, source_page: sourcePage });
      setStep("success");
    } catch (err) {
      setError(err.message || "Could not submit — please try again.");
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid={testId}
        onClick={() => onOpenChange(true)}
        style={style}
        className={className || "inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide text-[#5C6470] hover:text-[#0D1B2A] transition-colors"}
      >
        {label}
      </button>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="release-interest-modal" className="bg-[#FBF7EE] border-[#E5E1D3] max-w-sm rounded-sm">
          {step === "success" ? (
            <div data-testid="release-interest-success" className="py-4 text-center">
              <CheckCircle2 size={40} className="mx-auto" style={{ color: accentColor }} />
              <h3 className="mt-4 font-serif-d text-xl text-[#0D1B2A]">Thank you.</h3>
              <p className="mt-1 text-[15px] font-semibold text-[#0D1B2A]">We'll let you know when {seriesName} is ready.</p>
              <button
                type="button"
                data-testid="release-interest-close-success"
                onClick={() => onOpenChange(false)}
                className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-3 text-[12px] font-semibold tracking-wide uppercase rounded-sm text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: accentColor }}
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit}>
              <DialogHeader>
                <DialogTitle className="text-[#0D1B2A] font-serif text-xl flex items-center gap-2">
                  <Bell size={18} style={{ color: accentColor }} /> Tell me when {seriesName} is ready
                </DialogTitle>
                <DialogDescription className="text-[#5C6470] text-sm pt-1">
                  We'll send you a short note the moment this series launches. No spam, no payment.
                </DialogDescription>
              </DialogHeader>

              <div className="mt-3">
                <Label htmlFor="ri-name" className="text-[#0D1B2A] text-[13px] font-semibold">Name</Label>
                <Input
                  id="ri-name"
                  data-testid="release-interest-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1.5 border-[#D9D4C4] text-[#0D1B2A]"
                  autoFocus
                />
              </div>

              <div className="mt-4">
                <Label className="text-[#0D1B2A] text-[13px] font-semibold">Contact method</Label>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    data-testid="release-interest-contact-email-tab"
                    onClick={() => setContactMethod("email")}
                    className={`flex-1 py-2 text-[12px] font-semibold uppercase tracking-wide rounded-sm border transition-colors ${contactMethod === "email" ? "text-white" : "text-[#5C6470] border-[#D9D4C4]"}`}
                    style={contactMethod === "email" ? { backgroundColor: accentColor, borderColor: accentColor } : undefined}
                  >
                    Email
                  </button>
                  <button
                    type="button"
                    data-testid="release-interest-contact-whatsapp-tab"
                    onClick={() => setContactMethod("whatsapp")}
                    className={`flex-1 py-2 text-[12px] font-semibold uppercase tracking-wide rounded-sm border transition-colors ${contactMethod === "whatsapp" ? "text-white" : "text-[#5C6470] border-[#D9D4C4]"}`}
                    style={contactMethod === "whatsapp" ? { backgroundColor: accentColor, borderColor: accentColor } : undefined}
                  >
                    WhatsApp
                  </button>
                </div>
                {contactMethod === "email" ? (
                  <Input
                    type="email"
                    data-testid="release-interest-email-input"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 border-[#D9D4C4] text-[#0D1B2A]"
                  />
                ) : (
                  <Input
                    type="tel"
                    data-testid="release-interest-whatsapp-input"
                    placeholder="+91 9XXXXXXXXX"
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    className="mt-2 border-[#D9D4C4] text-[#0D1B2A]"
                  />
                )}
              </div>

              <label className="mt-4 flex items-start gap-2.5 text-[13px] text-[#5C6470] leading-[1.5] cursor-pointer">
                <Checkbox
                  data-testid="release-interest-consent-checkbox"
                  checked={consent}
                  onCheckedChange={(v) => setConsent(Boolean(v))}
                  className="mt-0.5"
                />
                I'd like LifeIQ to notify me when this series becomes available.
              </label>

              {error && (
                <p data-testid="release-interest-error" role="alert" className="mt-3 text-[13px] text-red-600">{error}</p>
              )}

              <button
                type="submit"
                data-testid="release-interest-submit-btn"
                disabled={submitting}
                style={{ backgroundColor: accentColor }}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[13px] font-semibold tracking-wide uppercase rounded-sm text-white hover:opacity-90 disabled:opacity-70 transition-opacity"
              >
                {submitting ? <Loader2 size={15} className="animate-spin" /> : null}
                {submitting ? "Submitting…" : "Notify Me"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
